// Formatos clínicos (medical.plantilla). Centro Medico → Catálogos → Formatos clínicos.
// El contenido (jsonb) aloja un schema { secciones:[{titulo,campos:[...] }] } que
// el renderer (FormularioClinico) pinta en la atención. Reemplaza el viejo editor SOAP.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import {
  Campo, CATEGORIAS_FORMATO, Especialidad, Modal, Plantilla, SeccionFormato, Switch, TipoCampo,
  fieldCls, med, medRpc,
} from './shared';

const TIPOS: { v: TipoCampo; label: string }[] = [
  { v: 'texto', label: 'Texto' }, { v: 'textarea', label: 'Texto largo' }, { v: 'numero', label: 'Número' },
  { v: 'fecha', label: 'Fecha' }, { v: 'select', label: 'Lista' }, { v: 'checkbox', label: 'Sí/No' }, { v: 'titulo', label: 'Subtítulo' },
];
const slug = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 40) || 'campo';

const vacio = { nombre: '', codigo: '', categoria: 'general', especialidad_id: '', descripcion: '', es_activo: true };

export function Plantillas() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [filtroCat, setFiltroCat] = useState('todas');
  const { data: rows = [], isFetching } = useQuery<Plantilla[]>({
    queryKey: ['med-formatos', filtroCat],
    queryFn: () => medRpc<Plantilla[]>('med_plantillas', { _categoria: filtroCat === 'todas' ? null : filtroCat }),
  });
  const { data: especialidades = [] } = useQuery<Especialidad[]>({
    queryKey: ['med-especialidades-sel'],
    queryFn: () => med.list<Especialidad>('especialidad', '?select=id,nombre&deleted_at=is.null&order=nombre'),
  });

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [secciones, setSecciones] = useState<SeccionFormato[]>([]);
  const [abierto, setAbierto] = useState(false);

  const abrir = (p?: Plantilla) => {
    if (p) {
      setEditId(p.id);
      setForm({ nombre: p.nombre, codigo: p.codigo ?? '', categoria: p.categoria ?? 'general', especialidad_id: p.especialidad_id ?? '', descripcion: p.descripcion ?? '', es_activo: p.es_activo });
      setSecciones(Array.isArray(p.contenido?.secciones) ? p.contenido.secciones : []);
    } else { setEditId(null); setForm(vacio); setSecciones([{ titulo: 'Datos', campos: [] }]); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: () => {
      // Cada campo (no separador) necesita una clave única y no vacía: es la
      // llave con la que se guarda la respuesta en evolucion.datos.
      const campos = secciones.flatMap((s) => s.campos).filter((c) => c.tipo !== 'titulo');
      if (campos.some((c) => !c.clave?.trim())) return Promise.reject(new Error('Cada campo necesita una etiqueta (genera su clave)'));
      const claves = campos.map((c) => c.clave);
      const dup = claves.find((k, i) => claves.indexOf(k) !== i);
      if (dup) return Promise.reject(new Error(`Hay campos con la misma clave: "${dup}". Renombra la etiqueta para diferenciarlos.`));
      return medRpc('med_plantilla_guardar', {
        _data: {
          id: editId, nombre: form.nombre, codigo: form.codigo || null, categoria: form.categoria,
          especialidad_id: form.especialidad_id || null, descripcion: form.descripcion || null,
          es_activo: form.es_activo, contenido: { secciones },
        },
      });
    },
    onSuccess: () => { toast.success('Formato guardado'); setAbierto(false); void qc.invalidateQueries({ queryKey: ['med-formatos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => medRpc('med_plantilla_eliminar', { _id: id }),
    onSuccess: () => { toast.success('Formato eliminado'); void qc.invalidateQueries({ queryKey: ['med-formatos'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const espNombre = (id: string | null) => especialidades.find((e) => e.id === id)?.nombre ?? '—';

  // editor de secciones/campos
  const setSec = (i: number, patch: Partial<SeccionFormato>) => setSecciones((ss) => ss.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  const addSec = () => setSecciones((ss) => [...ss, { titulo: 'Nueva sección', campos: [] }]);
  const delSec = (i: number) => setSecciones((ss) => ss.filter((_, j) => j !== i));
  const addCampo = (si: number) => setSec(si, { campos: [...secciones[si].campos, { clave: '', etiqueta: '', tipo: 'texto' }] });
  const setCampo = (si: number, ci: number, patch: Partial<SeccionFormato['campos'][number]>) =>
    setSec(si, { campos: secciones[si].campos.map((c, j) => (j === ci ? { ...c, ...patch } : c)) });
  const delCampo = (si: number, ci: number) => setSec(si, { campos: secciones[si].campos.filter((_, j) => j !== ci) });
  const totalCampos = useMemo(() => secciones.reduce((n, s) => n + s.campos.length, 0), [secciones]);

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">Formatos clínicos</h1>
        <div className="flex items-center gap-2">
          <select className={`${fieldCls} w-44`} value={filtroCat} onChange={(e) => setFiltroCat(e.target.value)}>
            <option value="todas">Todas las categorías</option>
            {CATEGORIAS_FORMATO.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo formato</Button>
        </div>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Formatos ({rows.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Código</th><th className="py-2 pr-3">Nombre</th><th className="py-2 pr-3">Categoría</th>
              <th className="py-2 pr-3">Especialidad</th><th className="py-2 pr-3 text-right">Campos</th><th className="py-2 pr-3">Estado</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin formatos</td></tr>}
              {rows.map((p) => (
                <tr key={p.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono text-xs">{p.codigo ?? '—'}</td>
                  <td className="py-2 pr-3 font-medium">{p.nombre}</td>
                  <td className="py-2 pr-3"><span className="rounded-full bg-muted px-2 py-0.5 text-xs">{p.categoria ?? 'general'}</span></td>
                  <td className="py-2 pr-3 text-muted-foreground">{espNombre(p.especialidad_id)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{p.campos ?? 0}</td>
                  <td className="py-2 pr-3">{p.es_activo ? 'Activo' : 'Inactivo'}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title="Editar" onClick={() => abrir(p)}><Pencil className="w-4 h-4" /></button>
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 ml-1" title="Eliminar"
                      onClick={async () => { if (!(await dlg.confirmar(`¿Eliminar "${p.nombre}"?`, { peligro: true }))) return; eliminar.mutate(p.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar formato' : 'Nuevo formato'} onClose={() => setAbierto(false)} wide>
          <div className="space-y-4 max-h-[72vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Nombre *"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} /></Campo>
              <Campo label="Código"><input className={fieldCls} value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} placeholder="MSP-002, R-077…" /></Campo>
              <Campo label="Categoría">
                <select className={fieldCls} value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}>
                  {CATEGORIAS_FORMATO.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Campo>
              <Campo label="Especialidad">
                <select className={fieldCls} value={form.especialidad_id} onChange={(e) => setForm({ ...form, especialidad_id: e.target.value })}>
                  <option value="">General</option>{especialidades.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                </select>
              </Campo>
            </div>
            <Campo label="Descripción"><input className={fieldCls} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></Campo>

            {/* Editor de secciones/campos */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase text-muted-foreground">Estructura del formato ({totalCampos} campos)</p>
                <Button size="sm" variant="outline" onClick={addSec}><Plus className="w-4 h-4 mr-1" /> Sección</Button>
              </div>
              {secciones.map((sec, si) => (
                <div key={si} className="rounded-md border p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <input className={`${fieldCls} font-medium`} value={sec.titulo} onChange={(e) => setSec(si, { titulo: e.target.value })} placeholder="Título de la sección" />
                    <button className="rounded p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title="Quitar sección" onClick={() => delSec(si)}><Trash2 className="w-4 h-4" /></button>
                  </div>
                  {sec.campos.map((c, ci) => (
                    <div key={ci} className="grid grid-cols-[1fr_8rem_1fr_2rem] gap-2 items-center">
                      <input className={fieldCls} value={c.etiqueta} placeholder="Etiqueta"
                        onChange={(e) => setCampo(si, ci, { etiqueta: e.target.value, clave: c.clave || slug(e.target.value) })} />
                      <select className={fieldCls} value={c.tipo} onChange={(e) => setCampo(si, ci, { tipo: e.target.value as TipoCampo })}>
                        {TIPOS.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                      </select>
                      {c.tipo === 'select'
                        ? <input className={fieldCls} value={(c.opciones ?? []).join(', ')} placeholder="Opciones separadas por coma"
                            onChange={(e) => setCampo(si, ci, { opciones: e.target.value.split(',').map((o) => o.trim()).filter(Boolean) })} />
                        : <input className={fieldCls} value={c.unidad ?? ''} placeholder="Unidad (opcional)" onChange={(e) => setCampo(si, ci, { unidad: e.target.value })} />}
                      <button className="rounded p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" title="Quitar campo" onClick={() => delCampo(si, ci)}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <button className="text-xs text-primary hover:underline" onClick={() => addCampo(si)}>+ Agregar campo</button>
                </div>
              ))}
              {secciones.length === 0 && <p className="text-sm text-muted-foreground">Agrega una sección para empezar. (Las plantillas SOAP antiguas no tienen secciones; al guardarlas aquí pasan a formato estructurado.)</p>}
            </div>

            <div className="flex items-center justify-between border-t pt-3">
              <Switch checked={form.es_activo} onChange={(v) => setForm({ ...form, es_activo: v })} label="Activo" />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
                <Button isLoading={guardar.isPending} disabled={!form.nombre.trim()} onClick={() => guardar.mutate()}>Guardar</Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
